import { Input, Select, Table, Tooltip, message } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Play } from "lucide-react";

import { logOutUser } from "../../../common";
import {
  AUTH_TOKEN,
  ORG_ID,
  REACT_APP_API_DID_URL,
  REACT_APP_PAM_API,
  DOMAIN_ID,
} from "../../../constants";
import { useDebounce } from "../../../common/debounce";
import { formatTimeByTimezone } from "../../../common/formatTimebyTimezone";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import {
  EmbeddedShell,
  FilterCard,
  FilterField,
  MonoCell,
  StatusPill,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../../design-system/tableUi";
import { CopyButton } from "../../../pages/enduser/ssh-proxy/sshProxyUi";
import { EMPTY, formatBytes, orDash } from "../../../pages/enduser/ssh-proxy/sshProxyModel";
import {
  listSessionRecording,
} from "../../../service/sshProxyService";

// Session recordings — text.
//
// This screen was built for the Guacamole web-terminal flow and now carries SSH
// proxy sessions too. The two describe themselves differently: every field BE-1
// added is `omitempty`, so a Guacamole row simply omits them. A missing fact is
// rendered as an em dash, never as blank space that reads like a bug.
//
// `recording_mime_type` is named for MIME types and does not hold them — its
// values are the format tokens `typescript`, `asciicast` and `guac`, and the
// listing filters on exactly one of them. The proxy briefly reported the real
// MIME type (application/x-asciicast) and its rows matched no listing at all
// while being stored perfectly. If a count says zero when you know a session
// happened, that mismatch is the first place to look — which is why the format
// is a visible control here rather than a constant buried in the payload.
const FORMATS = [
  { value: "asciicast", label: "SSH gateway (asciicast)" },
  { value: "typescript", label: "Web terminal (typescript)" },
];

// targetHost / targetAccount / jumpServerId are indexed server-side and compare
// with `=`, NOT ILIKE — a partial hostname matches nothing rather than matching
// loosely. The value inputs say so, because "no results" from a half-typed host
// otherwise reads as "there were no sessions".
const FILTER_FIELDS = [
  { value: "userName", label: "Endpoint user", source: "endpointUser" },
  { value: "targetHost", label: "Target host", source: "text", exact: true },
  { value: "targetAccount", label: "Target account", source: "text", exact: true },
  { value: "emailAddress", label: "User", source: "users" },
];

const PAGESIZE = 10;
const mono = { fontFamily: AN_MONO, fontSize: 12 };

// A cell that admits it has nothing rather than looking empty.
const Dash = () => <span style={{ color: AN_COLORS.muted }}>{EMPTY}</span>;

function textCell(value, useMono) {
  const v = orDash(value);
  if (v === EMPTY) return <Dash />;
  return useMono ? <MonoCell>{v}</MonoCell> : <span style={{ whiteSpace: "nowrap", fontSize: 12.5 }}>{v}</span>;
}

const TextRecording = () => {
  const [searchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [notification, notificationCtx] = message.useMessage();
  const [sessionData, setSessionData] = useState([]);
  const [format, setFormat] = useState("asciicast");

  // Deep links: ?host= from the Endpoints table (the endpoint's address, which
  // is what a proxy recording stores as its target host), ?epmUser= from the
  // user screens.
  //
  // There is no Endpoint filter: proxy recordings store `endpoint` as
  // account@host, so matching it against an endpoint's display hostname never
  // hit, and the picker listed every duplicate epm_machines row.
  const deepHost = searchParams.get("host");
  const deepUser = searchParams.get("epmUser");
  const [filterBy, setFilterBy] = useState(deepHost ? "targetHost" : deepUser ? "userName" : "");
  const [filterValue, setFilterValue] = useState(deepUser || "");
  const [filterText, setFilterText] = useState(deepHost || "");

  // ?session= arrives from the Auth Logs table (an SSH row's session id), and
  // ?sessionId= from onboarding. Either narrows the listing server-side on
  // filter.sessionId — an exact match on jump_server_recordings.session_id that
  // also accepts the "authnull-sshproxy:<id>:<account>" form from auth.log — so
  // the hit is found even when it is not on the first page. It rides alongside
  // the field filter above rather than replacing it.
  const [sessionFilter, setSessionFilter] = useState(
    (searchParams.get("session") || searchParams.get("sessionId") || "").trim(),
  );

  const [endpointUser, setEndpointUser] = useState([]);
  const [userData, setUserData] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageModel, setCurrentPageModel] = useState({ pageSize: PAGESIZE, page: 0 });

  const navigate = useNavigate();

  const debouncedSearch = useDebounce(search, 500);
  const debouncedFilterText = useDebounce(filterText, 500);

  const activeField = FILTER_FIELDS.find((f) => f.value === filterBy);
  const effectiveFilterValue = activeField?.source === "text" ? debouncedFilterText : filterValue;

  const fetchSessionRecording = useCallback(() => {
    setIsLoading(true);
    listSessionRecording({
      payload: {
        orgId: ORG_ID(),
        domainId: DOMAIN_ID(),
        pageId: currentPage + 1,
        pageSize: currentPageModel.pageSize,
        search: debouncedSearch,
        filter: {
          filterBy,
          value: effectiveFilterValue,
          type: "recordingmimetype",
          recordingMimeType: format,
          ...(sessionFilter ? { sessionId: sessionFilter } : {}),
        },
      },
      onSuccess: (data) => {
        setIsLoading(false);
        setTotalCount(data?.total ?? 0);
        // The table needs a stable id per row. `id` is the recording's primary
        // key and is what getRecordingContent takes; sessionId is the proxy's
        // own id and is not unique across a re-recorded session.
        setSessionData(
          (data?.sessionRecordings ?? []).map((r, i) => ({ ...r, _rowId: r.id ?? r.sessionId ?? `row-${i}` })),
        );
        setCurrentPageModel({ pageSize: PAGESIZE, page: currentPage });
      },
      onFailure: (err) => {
        setIsLoading(false);
        if (err?.response?.data?.message === "Invalid credentials" || err?.response?.status === 401) {
          notification.open({ type: "error", content: "Credentials are invalid" });
          logOutUser();
        } else {
          notification.open({ type: "error", content: "Unable to fetch session recording" });
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, filterBy, effectiveFilterValue, format, sessionFilter]);

  useEffect(() => {
    fetchSessionRecording();
  }, [fetchSessionRecording]);

  useEffect(() => {
    fetchEndpointUser();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 6 of the onboarding wizard links straight here with a session id, so
  // the "did it actually work?" answer opens on the recording itself.
  // A recording plays on its own page, so the console gets the whole viewport.
  // The row rides along only for older rows whose bytes sit behind recordingUrl.
  const openRecording = (row, { replace = false } = {}) =>
    navigate(`/pam/jumpServer-sessionRecordings/play/${encodeURIComponent(row.id ?? row._rowId)}`, {
      replace,
      state: { row },
    });

  // ?sessionId= deep link: open that session's player once the list has it.
  const deepLinkSession = searchParams.get("sessionId");
  useEffect(() => {
    if (!deepLinkSession) return;
    const hit = sessionData.find((r) => r.sessionId === deepLinkSession);
    if (hit) openRecording(hit, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkSession, sessionData]);

  // ── lookups for the filter pickers ──────────────────────────────────────────
  const fetchEndpointUser = () => {
    axios
      .post(
        `${REACT_APP_PAM_API}/endpointUsers/listAllEpmUsers`,
        {
          domainId: DOMAIN_ID(),
          pageId: 1,
          pageSize: 100,
          token: AUTH_TOKEN(),
          orgId: ORG_ID(),
          filter: { filterBy: "", value: "" },
          instanceIds: [],
          search: "",
        },
        { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } },
      )
      .then((res) => {
        setEndpointUser((res?.data?.epmUsers || []).map((u) => ({ value: u.userName, label: u.userName })));
      })
      .catch((err) => {
        if (err?.response?.data?.message === "Invalid credentials" || err?.response?.status === 401) logOutUser();
      });
  };

  const fetchUsers = () => {
    axios
      .post(
        `${REACT_APP_API_DID_URL}/walletService/walletUserList`,
        { domainId: DOMAIN_ID(), pageNumber: 1, pageSize: 150, filter: "", orgId: ORG_ID() },
        { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } },
      )
      .then((res) => {
        setUserData(
          (res?.data?.users || [])
            .filter((u) => u.emailAddress)
            .map((u) => ({ value: u.emailAddress, label: u.emailAddress })),
        );
      })
      .catch(() => {});
  };

  const filterOptions = useMemo(() => {
    if (activeField?.source === "endpointUser") return endpointUser;
    if (activeField?.source === "users") return userData;
    return [];
  }, [activeField, endpointUser, userData]);

  // ── columns ─────────────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        // First, so it's reachable without scrolling a wide table sideways.
        // antd passes the row itself as the second argument — the old
        // `val.row` was a DataGrid leftover, undefined here, so Play threw
        // before any request went out.
        title: "Recording",
        key: "view",
        width: 90,
        fixed: "left",
        render: (_, row) => (
          <button
            type="button"
            onClick={() => openRecording(row)}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, border: "none", background: "none", padding: 0, color: AN_COLORS.primary, cursor: "pointer", fontFamily: AN_FONT, fontSize: 13 }}
          >
            <Play size={12} fill="currentColor" />
            Play
          </button>
        ),
      },
      {
        // Second and pinned with Play: when it happened is what people scan
        // for, and it stays in view while the wide table scrolls sideways.
        title: "Session time",
        key: "sessionRecordingTime",
        width: 190,
        fixed: "left",
        render: (_, val) => {
          const ts = val.sessionRecordingTime;
          if (!ts) return <Dash />;
          return formatTimeByTimezone(ts, localStorage.getItem("timezone"));
        },
      },
      {
        // The same id appears in the TARGET machine's own /var/log/auth.log:
        //   Accepted publickey for ubuntu ... ID authnull-sshproxy:<id>:ubuntu
        // When a customer asks us to prove a session from their logs, this is
        // the answer — so it is one click to copy, not a string to retype.
        title: "Session ID",
        key: "sessionId",
        width: 210,
                render: (_, val) => {
          const id = val.sessionId;
          if (!id) return <Dash />;
          return (
            <Tooltip title="Also written to the target host's /var/log/auth.log as authnull-sshproxy:<id>:<account>">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ ...mono, color: AN_COLORS.ink }}>{id}</span>
                <CopyButton text={id} label="" />
              </span>
            </Tooltip>
          );
        },
      },
      {
        // userId null means no registered SSH key matched the fingerprint that
        // authenticated the session. The backend stores null rather than
        // guessing: a wrong name in an audit trail is worse than a missing one.
        title: "Operator",
        key: "userName",
        width: 190,
        render: (_, val) => {
          const { userId, userName } = val || {};
          if (!userId) {
            return (
              <Tooltip
                title={`No registered SSH key matched the fingerprint that authenticated this session${
                  userName ? `. The gateway recorded the login name "${userName}".` : "."
                } We do not put a guessed name in an audit trail.`}
              >
                <span>
                  <StatusPill tone="warn" status="unattributed" label="Unattributed" />
                </span>
              </Tooltip>
            );
          }
          return textCell(userName);
        },
      },
      { title: "Target host", field: "targetHost", width: 160, render: (_, v) => textCell(v.targetHost, true) },
      {
        // Separate facts on purpose: on a shared account ten people are
        // `oracle`, and the host is what distinguishes what each of them
        // reached. Never concatenated into the free-text endpoint field.
        title: "Target account",
        key: "targetAccount",
        width: 140,
        render: (_, v) => textCell(v.targetAccount, true),
      },
      { title: "Endpoint", field: "endpoint", width: 150, render: (_, v) => textCell(v.endpoint) },
      {
        title: "User",
        key: "emailAddress",
        width: 200,
        render: (_, val) =>
          val.emailAddress ? <span style={{ fontSize: 12.5, color: AN_COLORS.ink2 }}>{val.emailAddress}</span> : <Dash />,
      },
      { title: "Identity", field: "identityMethod", width: 130, render: (_, v) => textCell(v.identityMethod) },
      { title: "Presence", field: "presenceMethod", width: 130, render: (_, v) => textCell(v.presenceMethod) },
      {
        title: "Size",
        key: "sizeBytes",
        width: 90,
        render: (_, val) => {
          const s = formatBytes(val.sizeBytes);
          return s ? <span style={mono}>{s}</span> : <Dash />;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const applied = [
    { key: "format", label: `Format: ${FORMATS.find((f) => f.value === format)?.label}` },
    sessionFilter && {
      key: "session",
      label: `Session: ${sessionFilter}`,
      clear: () => {
        setSessionFilter("");
        setCurrentPage(0);
      },
    },
    filterBy && effectiveFilterValue && {
      key: "field",
      label: `${activeField?.label}: ${effectiveFilterValue}`,
      clear: () => {
        setFilterBy("");
        setFilterValue("");
        setFilterText("");
      },
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setFilterBy("");
    setFilterValue("");
    setFilterText("");
    setSessionFilter("");
    setCurrentPage(0);
  };


  return (
    <EmbeddedShell>
      {notificationCtx}

      <FilterCard
        columns="1fr 1fr 1fr 1.4fr"
        applied={applied}
        onClearAll={clearFilters}
        showing={sessionData.length}
        total={totalCount}
      >
        <FilterField label="Format">
          <Select
            value={format}
            onChange={(v) => {
              setFormat(v);
              setCurrentPage(0);
            }}
            options={FORMATS}
            style={{ width: "100%", height: 36 }}
          />
        </FilterField>

        <FilterField label="Filter by">
          <Select
            allowClear
            placeholder="Field"
            value={filterBy || undefined}
            onChange={(v) => {
              setFilterBy(v || "");
              setFilterValue("");
              setFilterText("");
              setCurrentPage(0);
            }}
            options={FILTER_FIELDS.map((f) => ({ value: f.value, label: f.label }))}
            style={{ width: "100%", height: 36 }}
          />
        </FilterField>

        <FilterField label="Value">
          {activeField?.source === "text" ? (
            <Tooltip title="Exact match — the server compares with = , not a substring search. A partial host matches nothing.">
              <Input
                placeholder={filterBy === "targetHost" ? "10.0.0.5 (exact)" : "ubuntu (exact)"}
                allowClear
                value={filterText}
                disabled={!filterBy}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setCurrentPage(0);
                }}
                style={{ height: 36 }}
              />
            </Tooltip>
          ) : (
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={filterBy ? "Select a value" : "Pick a field first"}
              disabled={!filterBy}
              value={filterValue || undefined}
              onChange={(v) => {
                setFilterValue(v || "");
                setCurrentPage(0);
              }}
              options={filterOptions}
              style={{ width: "100%", height: 36 }}
            />
          )}
        </FilterField>

        <FilterField label="Search">
          <Input
            placeholder="Search"
            allowClear
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(0);
            }}
            style={{ height: 36 }}
          />
        </FilterField>
      </FilterCard>

      {!isLoading && totalCount === 0 && (
        <div style={{ display: "flex", gap: 9, alignItems: "flex-start", background: "var(--an-infoBg)", border: "1px solid var(--an-infoBd)", color: "var(--an-infoFg)", borderRadius: 8, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.5, fontFamily: AN_FONT, marginBottom: 14 }}>
          <AlertTriangle size={15} style={{ marginTop: 1, flexShrink: 0 }} />
          <div>
            No <strong>{FORMATS.find((f) => f.value === format)?.label}</strong> recordings match. If you know a session
            happened, try the other format first — rows are stored under a format token (<code style={mono}>asciicast</code>{" "}
            / <code style={mono}>typescript</code>), and a session filed under the other one is invisible here, not
            missing.
          </div>
        </div>
      )}

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={sessionData}
          rowKey="_rowId"
          loading={isLoading}
          locale={tableEmpty(isLoading, "No recordings found.")}
          scroll={{ x: "max-content" }}
          pagination={{
            current: currentPage + 1,
            pageSize: PAGESIZE,
            total: totalCount,
            showSizeChanger: false,
            onChange: (p) => setCurrentPage(p - 1),
          }}
        />
      </TableCard>


    </EmbeddedShell>
  );
};

export default TextRecording;
